import {Injectable} from '@angular/core';
import {
    LocalScreenshotId,
    ProjectId,
    ScreenshotId,
    ScreenshotDataUrl,
    ImageUrl
} from '@webmate-web-core/shared/types/ids';
import {Observable, Subject} from 'rxjs';
import {ImageApi, ImageMetadata, ImageOverlay, ImageVariantName} from '@webmate-web-core/shared/api/rest/image.restapi';
import {map, mergeMap} from 'rxjs/operators';
import {IndexedDbService} from '../../../workbench/shared/services/indexed-db.service';
import md5 from 'md5';
import {dataUrlToFile} from '@webmate-web-core/shared/utils/image-dataurl';

export interface ImageDownloadRequest {
    name: string,
    imageUrl?: ImageUrl;
    imageId?: ScreenshotId,
    localScreenshotId?: LocalScreenshotId
}

@Injectable({
    providedIn: 'root'
})
export class ImageService {

    private downloadRequest = new Subject<ImageDownloadRequest>();
    
    downloadRequest$: Observable<ImageDownloadRequest> = this.downloadRequest.asObservable();

    constructor(private api: ImageApi,
                private indexedDB: IndexedDbService) {}

    // Download request is fulfilled by the image component based on how the image is displayed i.e with/wo overlays
    requestDownload(downloadReq: ImageDownloadRequest) {
       this.downloadRequest.next(downloadReq);
    }

    // Construct an authorized url from an ScreenshotId to be used as an img src
    getImageUrl(imageId: ScreenshotId, variantName?: ImageVariantName, asAttachment?: boolean, attachmentName?: string): ImageUrl {
        return this.api.getImageUrl(imageId, variantName, asAttachment, attachmentName);
    }

    // Get the MetaData for an Image
    getImageMetadata(imageId: ScreenshotId, variantName?: ImageVariantName): Observable<ImageMetadata | null> {
        return this.api.getImageMetadata(imageId, variantName);
    }

    // Persist a screenshot as a data url in the local indexedDB and return a local ID
    // This ID is only known to the browser
    storeLocalScreenshot(dataurl: string): Observable<LocalScreenshotId> {
        const id = md5(dataurl).slice(0,8);
        return this.indexedDB.addScreenshot(id, dataurl).pipe(map(() => id));
    }

    // Retrieve a locally stored screenshot from the IndexedDB
    getLocalScreenshot(id: LocalScreenshotId): Observable<ScreenshotDataUrl> {
        return this.indexedDB.getScreenshot(id);
    }

    removeLocalScreenshot(id: LocalScreenshotId): Observable<any> {
        return this.indexedDB.removeScreenshot(id);
    }

    convertImageUrlToDataUrl(imageUrl: string): Observable<ScreenshotDataUrl> {
        return new Observable<ScreenshotDataUrl>(observer => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
    
                if (!ctx) {
                    observer.error(new Error('Failed to get canvas context'));
                    return;
                }
                
                canvas.width = img.width;
                canvas.height = img.height;
                
                ctx.drawImage(img, 0, 0);
                
                try {
                    const dataUrl = canvas.toDataURL('image/png');
                    observer.next(dataUrl);
                    observer.complete();
                } catch (error) {
                    observer.error(error);
                }
            };
            
            img.onerror = (error) => {
                observer.error(new Error(`Failed to load image from URL: ${imageUrl}. Error: ${error}`));
            };
            
            img.src = imageUrl;
            
            return () => {
                img.onload = null;
                img.onerror = null;
            };
        });
    }

    // Store an image encoded as a file in the backend
    storeImage(img: File, projectId: ProjectId): Observable<any> {
        return this.api.storeImage(img, projectId);
    }

    // Store an url encoded image in the backend
    storeImageFromUrl(url: ScreenshotDataUrl, filename: string, projectId: ProjectId): Observable<any> {
        return this.storeImage(dataUrlToFile(url, filename), projectId);
    }

    // Store a local screenshot in the backend
    persistLocalScreenshot(localScreenshotId: LocalScreenshotId, filename: string, projectId: ProjectId) {
        return this.indexedDB.getScreenshot(localScreenshotId).pipe(mergeMap(dataurl => this.storeImageFromUrl(dataurl, filename, projectId)));
    }

    // Update the name of an image in the backend
    setImageName(id: ScreenshotId, name: string): Observable<void> {
        return this.api.setImageName(id, name);
    }

    // Update the overlays of an image
    setImageOverlays(id: ScreenshotId, overlays: ImageOverlay[]): Observable<void> {
        return this.api.setImageOverlays(id, overlays);
    }

}
